import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { formatAddressDisplay, getAddressTypeLabel } from "utils/addressHelpers";
import apis from "context/api";

/* eslint-disable react/prop-types */
function AddressGrid({
  addresses,
  onEdit,
  onDelete,
  customerId,
  onAddClick,
  defaultShipToAddressId,
  defaultBillToAddressId,
  onDefaultsChange,
}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedShipTo, setSelectedShipTo] = useState(defaultShipToAddressId);
  const [selectedBillTo, setSelectedBillTo] = useState(defaultBillToAddressId);

  useEffect(() => {
    setSelectedShipTo(defaultShipToAddressId);
    setSelectedBillTo(defaultBillToAddressId);
  }, [defaultShipToAddressId, defaultBillToAddressId]);

  const handleDeleteClick = (address) => {
    setSelectedAddress(address);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAddress) return;

    setDeleting(true);
    try {
      await apis.deleteAddress(customerId, selectedAddress.id);
      setDeleteDialogOpen(false);
      onDelete(selectedAddress.id);
    } catch (error) {
      alert(`Failed to delete address: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedAddress(null);
  };

  const handleSetDefault = (address) => {
    if (address.type === "shipping") {
      setSelectedShipTo(address.id);
      if (onDefaultsChange) {
        onDefaultsChange({
          defaultShipToAddressId: address.id,
          defaultBillToAddressId: selectedBillTo,
        });
      }
    } else if (address.type === "billing") {
      setSelectedBillTo(address.id);
      if (onDefaultsChange) {
        onDefaultsChange({
          defaultShipToAddressId: selectedShipTo,
          defaultBillToAddressId: address.id,
        });
      }
    }
  };

  // Empty state
  if (!addresses || addresses.length === 0) {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={4}
      >
        <MDTypography variant="h6" color="text" mb={2}>
          No addresses added yet
        </MDTypography>
        <MDButton variant="gradient" color="info" onClick={onAddClick}>
          Add Address
        </MDButton>
      </MDBox>
    );
  }

  // Grid view
  return (
    <MDBox>
      <Grid container spacing={2} mb={3}>
        {addresses.map((address) => (
          <Grid item xs={12} sm={6} md={4} key={address.id}>
            <Card sx={{ p: 3, position: "relative", minHeight: 340 }}>
              {/* Type badge */}
              <MDBox mb={2.5}>
                <Chip
                  label={getAddressTypeLabel(address.type)}
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    fontSize: "13px",
                    height: "32px",
                  }}
                />
              </MDBox>

              {/* Action buttons */}
              <MDBox
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  display: "flex",
                  gap: 0.5,
                }}
              >
                <MDButton
                  size="small"
                  variant="text"
                  color="success"
                  onClick={() => handleSetDefault(address)}
                  sx={{ fontSize: "13px", p: 1, minHeight: "40px" }}
                >
                  Set Default
                </MDButton>
                <IconButton
                  size="medium"
                  color="info"
                  onClick={() => onEdit(address)}
                  sx={{ p: 0.75 }}
                >
                  <EditIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  size="medium"
                  color="error"
                  onClick={() => handleDeleteClick(address)}
                  sx={{ p: 0.75 }}
                >
                  <DeleteIcon fontSize="medium" />
                </IconButton>
              </MDBox>

              {/* Full address */}
              <MDTypography
                variant="body2"
                color="text"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  pr: 5,
                  mb: 2.5,
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                {formatAddressDisplay(address)}
              </MDTypography>

              {/* Default indicator */}
              {(selectedShipTo === address.id || selectedBillTo === address.id) && (
                <MDBox sx={{ borderTop: "1px solid #e0e0e0", pt: 2.5, mt: 2.5 }}>
                  {selectedShipTo === address.id && (
                    <MDTypography
                      variant="caption"
                      color="success"
                      sx={{ fontWeight: 600, fontSize: "13px" }}
                    >
                      ★ Default Shipping Address
                    </MDTypography>
                  )}
                  {selectedBillTo === address.id && (
                    <MDTypography
                      variant="caption"
                      color="success"
                      sx={{ fontWeight: 600, display: "block", fontSize: "13px" }}
                    >
                      ★ Default Billing Address
                    </MDTypography>
                  )}
                </MDBox>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add Address button */}
      <MDBox display="flex" justifyContent="center">
        <MDButton variant="gradient" color="info" onClick={onAddClick}>
          Add Address
        </MDButton>
      </MDBox>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Address?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            Are you sure you want to delete this address?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton color="warning" onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </MDButton>
          <MDButton color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
/* eslint-enable react/prop-types */

export default AddressGrid;
